import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Ban, CalendarDays, Edit3, Flag, Gift, MapPin, Phone, Sparkles, TrendingDown, TrendingUp, Trash2, Wallet } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import API from "../services/api";
import { getMediaUrl } from "../services/api";
import AdminLayout from "./AdminLayout";
import { AdminNotice, useAdminNotice } from "../components/AdminNotice";

const USER_TAG_OPTIONS = ["No Tag", "VIP Player", "Bookie", "High Value", "Follow Up", "No Deposit", "Demo ID", "Test"];
const PROFILE_TABS = ["bets", "dw", "bonus", "winnings", "banks", "casino"];
const emptyBankForm = {
  bank_name: "",
  account_holder: "",
  account_number: "",
  ifsc: "",
  is_default: true,
};

function AdminUserProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { notice, showNotice, clearNotice } = useAdminNotice();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() => getProfileTab(searchParams.get("tab")));
  const [editOpen, setEditOpen] = useState(false);
  const [walletOpen, setWalletOpen] = useState(false);
  const [expandedBetGroup, setExpandedBetGroup] = useState("");
  const [editBet, setEditBet] = useState(null);
  const [savingQuickSettings, setSavingQuickSettings] = useState(false);
  const [savingWallet, setSavingWallet] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [editForm, setEditForm] = useState({ name: "", phone: "", login_location: "", tag: "No Tag", withdraw_limit_per_day: "", ank_limit: "" });
  const [walletForm, setWalletForm] = useState({ mode: "deposit", amount: "", remark: "" });
  const [adminSettings, setAdminSettings] = useState({ deposit_quick_amounts: "200,300,500,1000,2000,5000" });
  const [bankForm, setBankForm] = useState(emptyBankForm);
  const [savingBank, setSavingBank] = useState(false);
  const [datePreset, setDatePreset] = useState("today");
  const [dateFrom, setDateFrom] = useState(new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const manualQuickAmounts = parseAdminQuickAmounts(adminSettings.deposit_quick_amounts);

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (datePreset !== "all" && dateFrom && dateTo) {
        params.set("date_from", dateFrom);
        params.set("date_to", dateTo);
      }
      const res = await API.get(`/admin/users/${id}/profile${params.toString() ? `?${params.toString()}` : ""}`);
      const next = res.data || null;
      setData(next);
      setEditForm({
        name: next?.user?.name || "",
        phone: next?.user?.phone || "",
        login_location: next?.user?.login_location || "",
        tag: next?.user?.tag || "No Tag",
        withdraw_limit_per_day: next?.user?.withdraw_limit_per_day != null ? String(next.user.withdraw_limit_per_day) : "",
        ank_limit: next?.user?.ank_limit != null ? String(next.user.ank_limit) : "",
      });
    } catch (err) {
      console.log(err);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, datePreset, dateTo, id]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    API.get("/admin/settings")
      .then((res) => setAdminSettings((current) => ({ ...current, ...(res.data || {}) })))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const nextTab = getProfileTab(searchParams.get("tab"));
    if (nextTab !== tab) setTab(nextTab);
  }, [searchParams, tab]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const toggleBlock = async () => {
    if (!data?.user) return;
    await API.put(`/admin/users/${data.user.id}`, {
      status: (data.user.status || "Active") === "Blocked" ? "Active" : "Blocked",
    });
    await loadProfile();
  };

  const toggleFlag = async () => {
    if (!data?.user) return;
    await API.put(`/admin/users/${data.user.id}`, {
      flagged: data.user.flagged ? 0 : 1,
    });
    await loadProfile();
  };

  const saveProfile = async () => {
    const payload = {
      ...editForm,
      ank_limit: editForm.ank_limit === "" ? null : Number(editForm.ank_limit),
      withdraw_limit_per_day: editForm.withdraw_limit_per_day === "" ? null : Number(editForm.withdraw_limit_per_day),
    };
    await API.put(`/admin/users/${id}`, payload);
    setEditOpen(false);
    await loadProfile();
  };

  const switchTab = (nextTab) => {
    const safeTab = getProfileTab(nextTab);
    setTab(safeTab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("tab", safeTab);
    setSearchParams(nextParams, { replace: true });
  };

  const openWallet = (mode = "deposit") => {
    setWalletForm({ mode, amount: "", remark: "" });
    setWalletOpen(true);
  };

  const submitWalletAction = async () => {
    try {
      setSavingWallet(true);
      await API.post("/admin/users/manual-transaction", {
        user_id: Number(id),
        mode: walletForm.mode,
        amount: walletForm.amount,
        remark: walletForm.remark,
      });
      setWalletOpen(false);
      await loadProfile();
      showNotice("success", "Wallet Updated", `Processed ${formatMoney(walletForm.amount)} for this user.`);
    } catch (err) {
      showNotice("error", "Wallet Failed", err?.response?.data?.message || "Unable to process wallet action");
    } finally {
      setSavingWallet(false);
    }
  };

  const saveQuickSettings = async () => {
    try {
      setSavingQuickSettings(true);
      await API.put(`/admin/users/${id}`, {
        tag: editForm.tag,
        ank_limit: editForm.ank_limit === "" ? null : Number(editForm.ank_limit),
        withdraw_limit_per_day: editForm.withdraw_limit_per_day === "" ? null : Number(editForm.withdraw_limit_per_day),
      });
      await loadProfile();
    } finally {
      setSavingQuickSettings(false);
    }
  };

  const toggleDwEntry = async (entryType, entryId, exclude) => {
    await API.post(`/admin/users/${id}/dw-entries/${entryType}/${entryId}/exclude`, { exclude: exclude ? 1 : 0 });
    await loadProfile();
  };

  const deleteWrongDwEntry = async (entryType, entryId, amount) => {
    const label = entryType === "withdraw" ? "withdraw" : "deposit";
    const ok = window.confirm(`Delete wrong ${label} entry #${entryId} for ${formatMoney(amount)}?\n\nThis reverses wallet balance and removes it from user/admin history.`);
    if (!ok) return;
    await API.delete(`/admin/users/${id}/dw-entries/${entryType}/${entryId}`);
    await loadProfile();
  };

  const openBetEdit = (item) => {
    setEditBet({
      id: item.id,
      number: String(item.number || ""),
      amount: String(Number(item.amount || 0)),
      label: `${item.market_name || "Bet"} / ${prettyType(item.type)}`,
    });
  };

  const saveBetEdit = async () => {
    if (!editBet?.id) return;
    try {
      await API.put(`/admin/bets/${editBet.id}`, {
        number: editBet.number,
        amount: Number(editBet.amount || 0),
      });
      setEditBet(null);
      await loadProfile();
      showNotice("success", "Bet Updated", "Bet number and amount updated.");
    } catch (err) {
      showNotice("error", "Bet Edit Failed", err?.response?.data?.message || "Unable to edit bet");
    }
  };

  const updateBankForm = (key, value) => {
    setBankForm((current) => ({ ...current, [key]: value }));
  };

  const saveUserBank = async () => {
    const payload = {
      ...bankForm,
      user_id: Number(id),
      bank_name: bankForm.bank_name.trim(),
      account_holder: bankForm.account_holder.trim(),
      account_number: bankForm.account_number.trim(),
      ifsc: bankForm.ifsc.trim().toUpperCase(),
    };
    if (!payload.bank_name || !payload.account_holder || !payload.account_number || !payload.ifsc) {
      showNotice("error", "Bank Details Required", "Enter complete bank details.");
      return;
    }
    try {
      setSavingBank(true);
      await API.post("/bank-accounts", payload);
      setBankForm(emptyBankForm);
      await loadProfile();
      showNotice("success", "Bank Saved", "Bank details saved.");
    } catch (err) {
      showNotice("error", "Bank Save Failed", err?.response?.data?.message || "Unable to save bank details");
    } finally {
      setSavingBank(false);
    }
  };

  const deleteUserBank = async (account) => {
    if (!window.confirm(`Delete ${account.bank_name || "bank"} details for this user?`)) return;
    try {
      await API.delete(`/bank-accounts/${account.id}`);
      await loadProfile();
      showNotice("success", "Bank Deleted", "Bank details deleted.");
    } catch (err) {
      showNotice("error", "Bank Delete Failed", err?.response?.data?.message || "Unable to delete bank details");
    }
  };

  const stats = data?.stats || {};
  const user = data?.user || {};
  const totalBalance = Number(user.balance || 0);
  const winningBalance = Number(user.winnings || 0);
  const depositBalance = Math.max(totalBalance - winningBalance, 0);
  const betList = useMemo(() => data?.lists?.bets || [], [data]);
  const groupedBetList = useMemo(() => groupUserBets(betList), [betList]);
  const dwList = data?.lists?.dw || [];
  const bonusList = data?.lists?.bonus || [];
  const winningsList = data?.lists?.winnings || [];
  const bankList = data?.lists?.banks || [];
  const casinoList = data?.lists?.casino || [];
  const globalAnkLimit = data?.settings?.default_ank_limit;
  const locationBits = parseLocation(user.login_location);
  const isMobile = viewportWidth <= 900;
  const sessionLabel = getSessionLabel(user);
  const resolvedLastLogin = user.last_login || user.device_last_seen_at || user.created_at;
  const resolvedLastActivity = stats.last_activity_at || stats.last_bet_at || stats.last_deposit_at || stats.last_withdraw_at || resolvedLastLogin;
  const riskLevel = getRiskLevel(stats);
  const averageBet = Number(stats.total_bets || 0) ? Number(stats.total_bet_amount || 0) / Number(stats.total_bets || 1) : 0;
  const peakTime = stats.last_bet_at ? new Date(stats.last_bet_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "-";
  const favoriteMarket = getFavoriteMarket(betList);
  const winRate = useMemo(() => {
    const total = Number(stats.total_bets || 0);
    if (!total) return "0%";
    return `${Math.round((Number(stats.won_bets || 0) / total) * 100)}%`;
  }, [stats.total_bets, stats.won_bets]);

  useEffect(() => {
    if (datePreset === "today") {
      const today = new Date().toISOString().slice(0, 10);
      setDateFrom(today);
      setDateTo(today);
    }
  }, [datePreset]);

  return (
    <AdminLayout>
      <section style={page}>
        <style>{keyboardWalletCss}</style>
        <AdminNotice notice={notice} onClose={clearNotice} />
        <div style={{ ...topRow, flexWrap: isMobile ? "wrap" : "nowrap", alignItems: isMobile ? "flex-start" : "center" }}>
          <button type="button" style={backButton} onClick={() => navigate("/admin/users")}>
            <ArrowLeft size={18} />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={title}>User Profile</h1>
            <p style={subtitle}>ID: {user.id || id}</p>
          </div>
          <button type="button" style={editButton} onClick={() => setEditOpen(true)}>
            <Edit3 size={16} /> Edit
          </button>
          <button type="button" style={depositButton} onClick={() => openWallet("deposit")}>
            <Wallet size={16} /> Deposit
          </button>
          <button type="button" style={flagButton} onClick={toggleFlag}>
            <Flag size={16} /> {user.flagged ? "Unflag" : "Flag"}
          </button>
          <button type="button" style={blockButton} onClick={toggleBlock}>
            <Ban size={16} /> {(user.status || "Active") === "Blocked" ? "Unblock" : "Block"}
          </button>
        </div>

        {loading && <section style={card}>Loading...</section>}
        {!loading && !data && <section style={card}>User profile not found.</section>}

        {!loading && data && (
          <>
            <div style={{ ...heroGrid, gridTemplateColumns: isMobile ? "1fr" : heroGrid.gridTemplateColumns }}>
              <section style={card}>
                <div style={{ ...profileHeader, flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "flex-start" : "stretch" }}>
                  <div style={{ ...profileIdentity, alignItems: isMobile ? "flex-start" : "center" }}>
                    {user.profile_image ? (
                      <img src={getMediaUrl(user.profile_image)} alt={user.name || "User"} style={avatarPhoto} />
                    ) : (
                      <span style={avatar}>{getInitial(user.name)}</span>
                    )}
                    <div>
                      <div style={nameRow}>
                        <strong style={name}>{user.name || "User"}</strong>
                        <span style={(user.status || "Active") === "Blocked" ? blockedBadge : activeBadge}>
                          {user.status || "Active"}
                        </span>
                      </div>
                  <div style={metaLine}>
                        <Phone size={14} />
                        <span>{user.phone || "-"}</span>
                      </div>
                      <div style={{ ...metaLine, marginTop: "6px" }}>
                        <span style={microPill(user.flagged ? "#fff7ed" : "#eff6ff", user.flagged ? "#c2410c" : "#2563eb")}>
                          {user.flagged ? "Flagged" : "Clean"}
                        </span>
                        <span style={microPill((user.status || "Active") === "Blocked" ? "#fee2e2" : "#dcfce7", (user.status || "Active") === "Blocked" ? "#dc2626" : "#15803d")}>
                          {(user.status || "Active") === "Blocked" ? "Blocked" : "Active"}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={tagBox}>{user.tag || (user.role === "admin" ? "Admin" : user.agent && user.agent !== "Direct" ? user.agent : "Direct")}</div>
                </div>

                <div style={{ ...profileInfoGrid, gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : profileInfoGrid.gridTemplateColumns }}>
                  <Info label="Joined" value={formatDate(user.created_at)} />
                  <Info label="Last Login" value={formatDateTime(resolvedLastLogin)} />
                  <Info label="Referral Code" value={user.referral_code || "-"} />
                  <Info label="Agent" value={user.agent || "Direct"} />
                  <Info label="Tag" value={user.tag || (user.flagged ? "Flagged" : "No Tag")} />
                  <Info label="Ank Limit" value={user.ank_limit != null ? String(user.ank_limit) : globalAnkLimit != null ? `Global (${globalAnkLimit})` : "Default"} />
                  <Info
                    label="Withdraw Limit"
                    value={
                      user.withdraw_limit_per_day === 0
                        ? "No limit"
                        : user.withdraw_limit_per_day
                          ? `${user.withdraw_limit_per_day} per day`
                          : "Default"
                    }
                  />
                </div>

                <div style={quickSettingsCard}>
                  <div style={{ ...quickSettingsGrid, gridTemplateColumns: isMobile ? "1fr" : quickSettingsGrid.gridTemplateColumns }}>
                    <label style={quickField}>
                      <span style={quickLabel}>Tag</span>
                      <select style={quickSelect} value={editForm.tag} onChange={(e) => setEditForm((c) => ({ ...c, tag: e.target.value }))}>
                        {USER_TAG_OPTIONS.map((tag) => <option key={tag}>{tag}</option>)}
                      </select>
                    </label>

                    <label style={quickField}>
                      <span style={quickLabel}>Ank Limit</span>
                      <div style={quickInputRow}>
                        <input
                          style={quickInput}
                          value={editForm.ank_limit}
                          onChange={(e) => setEditForm((c) => ({ ...c, ank_limit: e.target.value.replace(/[^\d]/g, "").slice(0, 4) }))}
                          placeholder="Set limit"
                        />
                        <span style={quickHint}>{editForm.ank_limit === "" ? `(global${globalAnkLimit != null ? ` ${globalAnkLimit}` : ""})` : "(manual)"}</span>
                      </div>
                    </label>

                    <div style={quickField}>
                      <span style={quickLabel}>Withdraw Limit</span>
                      <div style={withdrawSegmentRow}>
                        <button
                          type="button"
                          style={withdrawOption(editForm.withdraw_limit_per_day === "1")}
                          onClick={() => setEditForm((c) => ({ ...c, withdraw_limit_per_day: "1" }))}
                        >
                          1/Day
                        </button>
                        <button
                          type="button"
                          style={withdrawOption(editForm.withdraw_limit_per_day === "2")}
                          onClick={() => setEditForm((c) => ({ ...c, withdraw_limit_per_day: "2" }))}
                        >
                          2/Day
                        </button>
                        <button
                          type="button"
                          style={withdrawOption(editForm.withdraw_limit_per_day === "0")}
                          onClick={() => setEditForm((c) => ({ ...c, withdraw_limit_per_day: "0" }))}
                        >
                          No Limit
                        </button>
                      </div>
                    </div>
                  </div>

                  <div style={quickActionRow}>
                    <button type="button" style={quickSaveButton} onClick={saveQuickSettings} disabled={savingQuickSettings}>
                      {savingQuickSettings ? "Saving..." : "Save Limits"}
                    </button>
                  </div>
                </div>
              </section>

              <section style={card}>
                <div style={networkTitle}>Location & Network</div>
                <div style={{ ...networkGrid, gridTemplateColumns: isMobile ? "1fr" : networkGrid.gridTemplateColumns }}>
                  <Info label="City" value={locationBits.city} />
                  <Info label="State" value={locationBits.state} />
                  <Info label="ISP" value={locationBits.isp} />
                  <Info label="IP Address" value={locationBits.ip} />
                  <Info label="Login On" value={sessionLabel} />
                  <Info label="Device Seen" value={formatDateTime(user.device_last_seen_at)} />
                </div>
              </section>

              <section style={balanceCard}>
                <div style={balanceRow}>
                  <span>Deposit Balance</span>
                  <strong>{formatMoney(depositBalance)}</strong>
                </div>
                <div style={{ ...balanceRow, background: "#ecfdf5", color: "#15803d" }}>
                  <span>Winning Balance</span>
                  <strong>{formatMoney(winningBalance)}</strong>
                </div>
                <div style={totalBalanceRow}>
                  <span>Total Balance</span>
                  <strong>{formatMoney(totalBalance)}</strong>
                </div>
              </section>
            </div>

            <div style={{ ...metricGrid, gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : metricGrid.gridTemplateColumns }}>
              <MetricCard label="Total Deposit" value={formatMoney(stats.total_deposit)} color="#16a34a" />
              <MetricCard label="Total Withdraw" value={formatMoney(stats.total_withdraw)} color="#dc2626" />
              <MetricCard label="D/W" value={formatMoney((stats.total_deposit || 0) - (stats.total_withdraw || 0))} color="#2563eb" />
              <MetricCard label="Last Deposit" value={stats.last_deposit_amount ? formatMoney(stats.last_deposit_amount) : "-"} color="#0f172a" />
              <MetricCard label="Last Withdraw" value={stats.last_withdraw_amount ? formatMoney(stats.last_withdraw_amount) : "-"} color="#0f172a" />
              <MetricCard label="Win Rate" value={winRate} color="#7c3aed" />
            </div>

            <div style={{ ...metricGrid, gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : metricGrid.gridTemplateColumns }}>
              <MetricCard label="Total Bets" value={String(stats.total_bets || 0)} color="#2563eb" />
              <MetricCard label="Total Bet Amount" value={formatMoney(stats.total_bet_amount)} color="#7c3aed" />
              <MetricCard label="Total Win Amount" value={formatMoney(stats.total_win_amount)} color="#16a34a" />
              <MetricCard label="Won Bets" value={String(stats.won_bets || 0)} color="#15803d" />
              <MetricCard label="Lost Bets" value={String(stats.lost_bets || 0)} color="#dc2626" />
              <MetricCard label="Last Activity" value={formatDateTime(resolvedLastActivity)} color="#0f172a" />
            </div>

            <section style={card}>
              <div style={riskRow}>
                <span style={{ ...riskBadge, ...riskTone(riskLevel) }}>Risk {riskLevel}</span>
                <span>Win: <b>{winRate}</b></span>
                <span>Avg: <b>{formatMoney(averageBet)}</b></span>
                <span>Bets: <b>{stats.total_bets || 0}</b></span>
                <span>W/L: <b>{stats.won_bets || 0} / {stats.lost_bets || 0}</b></span>
                <span>D/W: <b>{formatMoney((stats.total_deposit || 0) - (stats.total_withdraw || 0))}</b></span>
                <span>Fav: <b>{favoriteMarket}</b></span>
                <span>Peak: <b>{peakTime}</b></span>
                <span><MapPin size={14} style={{ verticalAlign: "middle", marginRight: 4 }} />{locationBits.city} / {locationBits.state}</span>
              </div>
            </section>

            <section style={card}>
              <div style={{ ...filterBar, flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center" }}>
                <div style={filterTabs}>
                  <button type="button" style={datePreset === "all" ? activeFilterChip : filterChip} onClick={() => setDatePreset("all")}>All</button>
                  <button type="button" style={datePreset === "today" ? activeFilterChip : filterChip} onClick={() => setDatePreset("today")}>Today</button>
                  <button type="button" style={datePreset === "custom" ? activeFilterChip : filterChip} onClick={() => setDatePreset("custom")}>Calendar</button>
                </div>
                <div style={dateRangeRow}>
                  <span style={dateLabel}><CalendarDays size={14} /> From</span>
                  <input type="date" value={dateFrom} onChange={(e) => { setDatePreset("custom"); setDateFrom(e.target.value); }} style={dateInput} />
                  <span style={dateLabel}>To</span>
                  <input type="date" value={dateTo} onChange={(e) => { setDatePreset("custom"); setDateTo(e.target.value); }} style={dateInput} />
                </div>
              </div>

              <div style={{ ...tabRow, flexWrap: "wrap" }}>
                <button type="button" style={tab === "bets" ? activeTab : tabButton} onClick={() => switchTab("bets")}>
                  Bet List <span style={tabCount}>{groupedBetList.length}</span>
                </button>
                <button type="button" style={tab === "dw" ? activeTab : tabButton} onClick={() => switchTab("dw")}>
                  D/W <span style={tabCount}>{dwList.length}</span>
                </button>
                <button type="button" style={tab === "bonus" ? activeTab : tabButton} onClick={() => switchTab("bonus")}>
                  Bonus <span style={tabCount}>{bonusList.length}</span>
                </button>
                <button type="button" style={tab === "winnings" ? activeTab : tabButton} onClick={() => switchTab("winnings")}>
                  Winnings <span style={tabCount}>{winningsList.length}</span>
                </button>
                <button type="button" style={tab === "banks" ? activeTab : tabButton} onClick={() => switchTab("banks")}>
                  Bank <span style={tabCount}>{bankList.length}</span>
                </button>
                <button type="button" style={tab === "casino" ? activeTab : tabButton} onClick={() => switchTab("casino")}>
                  Casino <span style={tabCount}>{casinoList.length}</span>
                </button>
              </div>

              {tab === "bets" ? (
                <div style={tableWrap}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={th}>ID</th>
                        <th style={th}>Market</th>
                        <th style={th}>Type</th>
                        <th style={th}>Numbers</th>
                        <th style={th}>Amount</th>
                        <th style={th}>Status</th>
                        <th style={th}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedBetList.map((group) => {
                        const isOpen = expandedBetGroup === group.key;
                        return (
                        <React.Fragment key={group.key}>
                        <tr>
                          <td style={td}>
                            <button
                              type="button"
                              style={viewGroupButton}
                              onClick={() => setExpandedBetGroup(isOpen ? "" : group.key)}
                            >
                              {isOpen ? "Hide" : "View"}
                            </button>
                          </td>
                          <td style={td}>
                            <strong>{group.market_name || "-"}</strong>
                            <div style={groupSubline}>{group.session} / {group.dateLabel}</div>
                          </td>
                          <td style={td}>{group.baseType}</td>
                          <td style={td}>
                            <div style={numberChipWrap}>
                              {group.previewNumbers.map((number) => (
                                <span key={number} style={wideAdminNumberChip}>{number}</span>
                              ))}
                              {group.extraCount > 0 && <span style={moreCountChip}>+{group.extraCount}</span>}
                            </div>
                          </td>
                          <td style={td}>
                            <strong>{formatMoney(group.amount)}</strong>
                            <div style={mutedSubline}>{group.rows.length} entries / Payout {formatMoney(group.payout)}</div>
                          </td>
                          <td style={td}>
                            <span style={statusPill(group.status)}>{group.status}</span>
                          </td>
                          <td style={td}>
                            <div>{formatDateTime(group.latestAt)}</div>
                            <div style={mutedSubline}>Session: {group.session}</div>
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td style={td} colSpan={7}>
                              <div style={betGroupPanel}>
                                {group.rows.map((item) => (
                                  <div key={item.id} style={betGroupItem}>
                                    <span style={wideAdminNumberChip}>{item.number}</span>
                                    <span>{prettyType(item.type)}</span>
                                    <strong>{formatMoney(item.amount)}</strong>
                                    <span style={statusPill(item.status)}>{item.status || "pending"}</span>
                                    <span>{formatDateTime(item.created_at || item.date)}</span>
                                    <button type="button" style={smallBetEditButton} onClick={() => openBetEdit(item)}>
                                      Edit
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      )})}
                      {!groupedBetList.length && <tr><td style={emptyCell} colSpan={7}>No bets found</td></tr>}
                    </tbody>
                  </table>
                </div>
              ) : tab === "dw" ? (
                <div style={tableWrap}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={th}>Txn ID</th>
                        <th style={th}>Type</th>
                        <th style={th}>Amount</th>
                        <th style={th}>Status</th>
                        <th style={th}>Date</th>
                        <th style={th}>Remark</th>
                        <th style={th}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dwList.map((item) => (
                        <tr key={`${item.type}-${item.id}`}>
                          <td style={td}>{item.id}</td>
                          <td style={{ ...td, color: String(item.type || "").toLowerCase() === "withdraw" ? "#dc2626" : "#16a34a", fontWeight: 800 }}>{item.type}</td>
                          <td style={{ ...td, color: String(item.type || "").toLowerCase() === "withdraw" ? "#dc2626" : "#16a34a", fontWeight: 800 }}>{formatMoney(item.amount)}</td>
                          <td style={td}><span style={statusPill(item.status)}>{item.status || "-"}</span></td>
                          <td style={td}>{formatDateTime(item.created_at)}</td>
                          <td style={td}>
                            {item.remark || "-"}
                            {Number(item.exclude_from_reports || 0) > 0 ? <div style={mutedSublineRed}>Removed from D/W</div> : null}
                          </td>
                          <td style={td}>
                            <div style={entryActionStack}>
                              <button
                                type="button"
                                style={Number(item.exclude_from_reports || 0) > 0 ? restoreEntryButton : removeEntryButton}
                                onClick={() => toggleDwEntry(String(item.type || "").toLowerCase() === "withdraw" ? "withdraw" : "deposit", item.id, Number(item.exclude_from_reports || 0) ? 0 : 1)}
                              >
                                {Number(item.exclude_from_reports || 0) > 0 ? "Restore" : "Remove"}
                              </button>
                              <button
                                type="button"
                                style={deleteWrongEntryButton}
                                onClick={() => deleteWrongDwEntry(String(item.type || "").toLowerCase() === "withdraw" ? "withdraw" : "deposit", item.id, item.amount)}
                              >
                                Delete Wrong
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!dwList.length && <tr><td style={emptyCell} colSpan={7}>No deposit or withdraw records</td></tr>}
                    </tbody>
                  </table>
                </div>
              ) : tab === "bonus" ? (
                <div style={tableWrap}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={th}>Txn ID</th>
                        <th style={th}>Type</th>
                        <th style={th}>Amount</th>
                        <th style={th}>Status</th>
                        <th style={th}>Wallet After</th>
                        <th style={th}>Added By</th>
                        <th style={th}>Remark</th>
                        <th style={th}>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bonusList.map((item) => (
                        <tr key={`bonus-${item.id}`}>
                          <td style={td}>{item.id}</td>
                          <td style={td}>{prettyType(item.type)}</td>
                          <td style={{ ...td, color: "#16a34a", fontWeight: 900 }}>{formatMoney(item.amount)}</td>
                          <td style={td}><span style={statusPill(item.status)}>{item.status || "approved"}</span></td>
                          <td style={td}>{item.wallet_balance != null ? formatMoney(item.wallet_balance) : "-"}</td>
                          <td style={td}>{item.approved_by_name || item.approved_by_role || "-"}</td>
                          <td style={td}>{item.remark || item.ref_type || "-"}</td>
                          <td style={td}>{formatDateTime(item.created_at)}</td>
                        </tr>
                      ))}
                      {!bonusList.length && <tr><td style={emptyCell} colSpan={8}>No bonus records</td></tr>}
                    </tbody>
                  </table>
                </div>
              ) : tab === "winnings" ? (
                <div style={tableWrap}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={th}>ID</th>
                        <th style={th}>Market</th>
                        <th style={th}>Type</th>
                        <th style={th}>Number</th>
                        <th style={th}>Bid</th>
                        <th style={th}>Win</th>
                        <th style={th}>Result</th>
                        <th style={th}>Settled</th>
                      </tr>
                    </thead>
                    <tbody>
                      {winningsList.map((item) => (
                        <tr key={`win-${item.id}`}>
                          <td style={td}>{item.id}</td>
                          <td style={td}>{item.market_name || "-"}</td>
                          <td style={td}>{prettyType(item.type)}</td>
                          <td style={td}>{item.number}</td>
                          <td style={td}>{formatMoney(item.bid_amount)}</td>
                          <td style={{ ...td, color: "#16a34a", fontWeight: 800 }}>{formatMoney(item.win_amount)}</td>
                          <td style={td}>{item.result_value || "-"}</td>
                          <td style={td}>{formatDateTime(item.created_at)}</td>
                        </tr>
                      ))}
                      {!winningsList.length && <tr><td style={emptyCell} colSpan={8}>No winnings found</td></tr>}
                    </tbody>
                  </table>
                </div>
              ) : tab === "banks" ? (
                <div style={bankPanel}>
                  <section style={bankFormCard}>
                    <div>
                      <h3 style={sectionTitle}>Add Bank Details</h3>
                      <p style={sectionHint}>Saved here will be available for user withdrawals.</p>
                    </div>
                    <div style={{ ...bankFormGrid, gridTemplateColumns: isMobile ? "1fr" : bankFormGrid.gridTemplateColumns }}>
                      <label style={field}>
                        <span>Bank Name</span>
                        <input style={input} value={bankForm.bank_name} onChange={(e) => updateBankForm("bank_name", e.target.value)} placeholder="State Bank, HDFC..." />
                      </label>
                      <label style={field}>
                        <span>Account Holder</span>
                        <input style={input} value={bankForm.account_holder} onChange={(e) => updateBankForm("account_holder", e.target.value)} placeholder="Holder name" />
                      </label>
                      <label style={field}>
                        <span>Account Number</span>
                        <input style={input} value={bankForm.account_number} onChange={(e) => updateBankForm("account_number", e.target.value.replace(/\s/g, ""))} placeholder="Account number" inputMode="numeric" />
                      </label>
                      <label style={field}>
                        <span>IFSC</span>
                        <input style={input} value={bankForm.ifsc} onChange={(e) => updateBankForm("ifsc", e.target.value.toUpperCase().replace(/\s/g, ""))} placeholder="IFSC code" />
                      </label>
                    </div>
                    <div style={bankActionRow}>
                      <label style={checkLabel}>
                        <input type="checkbox" checked={bankForm.is_default} onChange={(e) => updateBankForm("is_default", e.target.checked)} />
                        Set as default
                      </label>
                      <button type="button" style={saveBankButton} onClick={saveUserBank} disabled={savingBank}>
                        {savingBank ? "Saving..." : "Save Bank Details"}
                      </button>
                    </div>
                  </section>

                  <div style={tableWrap}>
                    <table style={table}>
                      <thead>
                        <tr>
                          <th style={th}>Bank</th>
                          <th style={th}>Account Holder</th>
                          <th style={th}>Account / UPI</th>
                          <th style={th}>IFSC</th>
                          <th style={th}>Default</th>
                          <th style={th}>Added</th>
                          <th style={th}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bankList.map((item) => (
                          <tr key={`bank-${item.id}`}>
                            <td style={td}>{item.bank_name || "-"}</td>
                            <td style={td}>{item.account_holder || "-"}</td>
                            <td style={td}>{item.upi_id || item.account_number || "-"}</td>
                            <td style={td}>{item.ifsc || "-"}</td>
                            <td style={td}><span style={statusPill(item.is_default ? "approved" : "pending")}>{item.is_default ? "default" : "saved"}</span></td>
                            <td style={td}>{formatDateTime(item.created_at)}</td>
                            <td style={td}>
                              <button type="button" style={deleteBankButton} onClick={() => deleteUserBank(item)}>
                                <Trash2 size={14} /> Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                        {!bankList.length && <tr><td style={emptyCell} colSpan={7}>No bank details saved</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div style={tableWrap}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={th}>ID</th>
                        <th style={th}>Game</th>
                        <th style={th}>Round</th>
                        <th style={th}>Bet</th>
                        <th style={th}>Win</th>
                        <th style={th}>Multiplier</th>
                        <th style={th}>Status</th>
                        <th style={th}>Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {casinoList.map((item) => (
                        <tr key={item.row_key || `casino-${item.game_name}-${item.id}`}>
                          <td style={td}>{item.id}</td>
                          <td style={td}>
                            <strong>{item.game_name || "Aviator"}</strong>
                            {item.bet_type ? (
                              <div style={{ color: "#64748b", fontSize: "12px", marginTop: "2px" }}>
                                {String(item.bet_type).toUpperCase()} · {String(item.selection || "-").toUpperCase()}
                              </div>
                            ) : null}
                          </td>
                          <td style={td}>{item.round_id || "-"}</td>
                          <td style={td}>{formatMoney(item.bet_amount)}</td>
                          <td style={{ ...td, color: Number(item.win_amount || 0) > 0 ? "#16a34a" : "#0f172a", fontWeight: 800 }}>{formatMoney(item.win_amount)}</td>
                          <td style={td}>
                            {item.cashout_multiplier
                              ? `${Number(item.cashout_multiplier).toFixed(2)}x`
                              : item.crash_multiplier
                                ? `${Number(item.crash_multiplier).toFixed(2)}x`
                                : item.result_number !== null && item.result_number !== undefined
                                  ? `${item.result_number}${item.result_color ? ` (${item.result_color})` : ""}`
                                  : "-"}
                          </td>
                          <td style={td}><span style={statusPill(item.status)}>{item.status || "-"}</span></td>
                          <td style={td}>{formatDateTime(item.settled_at || item.created_at)}</td>
                        </tr>
                      ))}
                      {!casinoList.length && <tr><td style={emptyCell} colSpan={8}>No casino records for this date range</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}

        {editOpen && (
          <div style={overlay} onClick={() => setEditOpen(false)}>
            <div style={modal} onClick={(e) => e.stopPropagation()}>
              <h3 style={modalTitle}>Edit User</h3>
              <label style={field}>
                <span>Name</span>
                <input style={input} value={editForm.name} onChange={(e) => setEditForm((c) => ({ ...c, name: e.target.value }))} />
              </label>
              <label style={field}>
                <span>Phone</span>
                <input style={input} value={editForm.phone} onChange={(e) => setEditForm((c) => ({ ...c, phone: e.target.value }))} />
              </label>
              <label style={field}>
                <span>Login Location</span>
                <input style={input} value={editForm.login_location} onChange={(e) => setEditForm((c) => ({ ...c, login_location: e.target.value }))} />
              </label>
              <label style={field}>
                <span>Tag</span>
                <select style={input} value={editForm.tag} onChange={(e) => setEditForm((c) => ({ ...c, tag: e.target.value }))}>
                  {USER_TAG_OPTIONS.map((tag) => <option key={tag}>{tag}</option>)}
                </select>
              </label>
              <label style={field}>
                <span>Ank Limit</span>
                <input style={input} value={editForm.ank_limit} onChange={(e) => setEditForm((c) => ({ ...c, ank_limit: e.target.value }))} />
              </label>
              <label style={field}>
                <span>Withdraw Limit</span>
                <select
                  style={input}
                  value={editForm.withdraw_limit_per_day}
                  onChange={(e) => setEditForm((c) => ({ ...c, withdraw_limit_per_day: e.target.value }))}
                >
                  <option value="">Default</option>
                  <option value="1">Once per day</option>
                  <option value="2">Twice per day</option>
                  <option value="0">No limit</option>
                </select>
              </label>
              <div style={modalActions}>
                <button type="button" style={ghostButton} onClick={() => setEditOpen(false)}>Cancel</button>
                <button type="button" style={saveButton} onClick={saveProfile}>Save</button>
              </div>
            </div>
          </div>
        )}
        {walletOpen && (
          <div style={overlay} className="admin-keyboard-wallet-overlay" onClick={() => setWalletOpen(false)}>
            <form
              style={walletModal}
              className="admin-profile-keyboard-wallet-modal"
              onClick={(event) => event.stopPropagation()}
              onSubmit={(event) => {
                event.preventDefault();
                submitWalletAction();
              }}
            >
              <h3 style={modalTitle}>Wallet Action</h3>
              <div style={walletUserCard} className="admin-profile-keyboard-wallet-user">
                <span style={avatar}>{getInitial(user.name)}</span>
                <span>
                  <strong>{user.name || "User"}</strong>
                  <small style={walletUserMeta}>{user.phone || "-"}</small>
                </span>
                <strong style={walletBalance}>{formatMoney(user.balance)}</strong>
              </div>
              <div style={walletModeGrid} className="admin-keyboard-wallet-modes">
                <button type="button" style={walletForm.mode === "deposit" ? walletModeActive("#10b981") : walletModeButton} onClick={() => setWalletForm((current) => ({ ...current, mode: "deposit" }))}><TrendingUp size={16} /> Deposit</button>
                <button type="button" style={walletForm.mode === "winning" ? walletModeActive("#7c3aed") : walletModeButton} onClick={() => setWalletForm((current) => ({ ...current, mode: "winning" }))}><Sparkles size={16} /> Winning</button>
                <button type="button" style={walletForm.mode === "bonus" ? walletModeActive("#f59e0b") : walletModeButton} onClick={() => setWalletForm((current) => ({ ...current, mode: "bonus" }))}><Gift size={16} /> Bonus</button>
                <button type="button" style={walletForm.mode === "withdraw" ? walletModeActive("#ef4444") : walletModeButton} onClick={() => setWalletForm((current) => ({ ...current, mode: "withdraw" }))}><TrendingDown size={16} /> Withdraw</button>
              </div>
              <label style={field}>
                <span>Amount (Rs)</span>
                <input
                  style={input}
                  className="admin-keyboard-wallet-input"
                  value={walletForm.amount}
                  onChange={(event) => setWalletForm((current) => ({ ...current, amount: event.target.value.replace(/[^\d.]/g, "") }))}
                  placeholder="Enter amount"
                  inputMode="decimal"
                  enterKeyHint="done"
                  autoComplete="off"
                />
              </label>
              <div style={quickAmountRow} className="admin-keyboard-wallet-quick">
                {manualQuickAmounts.map((amount) => (
                  <button key={amount} type="button" style={quickAmountButton} onClick={() => setWalletForm((current) => ({ ...current, amount: String(amount) }))}>{formatQuickAmount(amount)}</button>
                ))}
              </div>
              <label style={field}>
                <span>Remark</span>
                <input
                  style={input}
                  className="admin-keyboard-wallet-input"
                  value={walletForm.remark}
                  onChange={(event) => setWalletForm((current) => ({ ...current, remark: event.target.value }))}
                  placeholder="Optional note"
                  enterKeyHint="done"
                  autoComplete="off"
                />
              </label>
              <div style={modalActions} className="admin-keyboard-wallet-footer">
                <button type="button" style={ghostButton} onClick={() => setWalletOpen(false)}>Cancel</button>
                <button type="submit" style={saveButton} disabled={savingWallet}>{savingWallet ? "Saving..." : "Submit"}</button>
              </div>
            </form>
          </div>
        )}

        {editBet && (
          <div style={overlay} onClick={() => setEditBet(null)}>
            <div style={modal} onClick={(e) => e.stopPropagation()}>
              <h3 style={modalTitle}>Edit Bet #{editBet.id}</h3>
              <p style={modalHint}>{editBet.label}</p>
              <label style={field}>
                <span>Bet Number</span>
                <input style={input} value={editBet.number} onChange={(e) => setEditBet((current) => ({ ...current, number: e.target.value.replace(/[^\d-]/g, "") }))} />
              </label>
              <label style={field}>
                <span>Amount</span>
                <input style={input} value={editBet.amount} inputMode="decimal" onChange={(e) => setEditBet((current) => ({ ...current, amount: e.target.value.replace(/[^\d.]/g, "") }))} />
              </label>
              <p style={modalHint}>Submit time stays same. Wallet difference is adjusted automatically.</p>
              <div style={modalActions}>
                <button type="button" style={ghostButton} onClick={() => setEditBet(null)}>Cancel</button>
                <button type="button" style={saveButton} onClick={saveBetEdit}>Save Bet</button>
              </div>
            </div>
          </div>
        )}
      </section>
    </AdminLayout>
  );
}

function getProfileTab(value) {
  return PROFILE_TABS.includes(value) ? value : "bets";
}

function getRiskLevel(stats) {
  const total = Number(stats.total_bets || 0);
  const lost = Number(stats.lost_bets || 0);
  if (!total) return "Low";
  const ratio = lost / total;
  if (ratio >= 0.8) return "High";
  if (ratio >= 0.5) return "Medium";
  return "Low";
}

function riskTone(level) {
  if (level === "High") return { background: "#fee2e2", color: "#dc2626" };
  if (level === "Medium") return { background: "#fef3c7", color: "#b45309" };
  return { background: "#dcfce7", color: "#15803d" };
}

function microPill(bg, color) {
  return {
    padding: "4px 8px",
    borderRadius: "999px",
    background: bg,
    color,
    fontWeight: 700,
    fontSize: "11px",
  };
}

function Info({ label, value }) {
  return (
    <div style={infoItem}>
      <span style={infoLabel}>{label}</span>
      <strong style={infoValue}>{value}</strong>
    </div>
  );
}

function MetricCard({ label, value, color }) {
  return (
    <article style={metricCard}>
      <span style={metricLabel}>{label}</span>
      <strong style={{ ...metricValue, color }}>{value}</strong>
    </article>
  );
}

function getInitial(name = "") {
  return name.trim().charAt(0).toUpperCase() || "U";
}

function formatMoney(value) {
  return `Rs ${Number(value || 0).toFixed(2)}`;
}

function parseAdminQuickAmounts(value) {
  const parsed = String(value || "200,300,500,1000,2000,5000")
    .split(/[,\n\r ]+/)
    .map((item) => Number.parseInt(item, 10))
    .filter((item) => Number.isFinite(item) && item > 0);
  const unique = Array.from(new Set(parsed));
  return unique.length ? unique.slice(0, 12) : [200, 300, 500, 1000, 2000, 5000];
}

function formatQuickAmount(value) {
  const amount = Number(value || 0);
  if (amount >= 1000 && amount % 1000 === 0) return `Rs ${amount / 1000}K`;
  return `Rs ${amount}`;
}

function prettyType(value = "") {
  return String(value || "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return `${date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}, ${date.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
}

function parseLocation(value) {
  const source = String(value || "").trim();
  if (!source) {
    return { city: "-", state: "-", isp: "-", ip: "-" };
  }

  const parts = source.split("|").map((item) => item.trim()).filter(Boolean);
  return {
    city: parts[0] || source,
    state: parts[1] || "-",
    isp: parts[2] || "-",
    ip: parts[3] || "-",
  };
}

function getFavoriteMarket(bets) {
  if (!Array.isArray(bets) || !bets.length) return "-";
  const counts = bets.reduce((acc, item) => {
    const key = item.market_name || "-";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || "-";
}

function groupUserBets(bets = []) {
  const groups = new Map();

  bets.forEach((item) => {
    const parsed = parseBetType(item.type);
    const marketKey = item.market_id || item.market_name || "market";
    const dateValue = String(item.bet_date || item.date || item.created_at || "").slice(0, 10) || "date";
    const timeSlot = item.time_slot || "";
    const key = [marketKey, dateValue, timeSlot || parsed.session].join("|");

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        market_name: item.market_name || "-",
        baseType: parsed.baseType,
        typeSet: new Set(),
        session: timeSlot || parsed.session,
        dateLabel: formatDate(dateValue),
        latestAt: item.created_at || item.date,
        amount: 0,
        payout: 0,
        rows: [],
        statusCounts: {},
      });
    }

    const group = groups.get(key);
    group.typeSet.add(parsed.baseType);
    group.rows.push(item);
    group.amount += Number(item.amount || 0);
    group.payout += Number(item.payout || 0);
    group.latestAt = group.latestAt && new Date(group.latestAt) > new Date(item.created_at || item.date || 0)
      ? group.latestAt
      : item.created_at || item.date || group.latestAt;
    const status = String(item.status || "pending").toLowerCase();
    group.statusCounts[status] = (group.statusCounts[status] || 0) + 1;
  });

  return Array.from(groups.values()).map((group) => {
    const status = group.statusCounts.won ? "won" : group.statusCounts.pending ? "pending" : group.statusCounts.lost ? "lost" : "mixed";
    const uniqueNumbers = Array.from(new Set(group.rows.map((item) => String(item.number || "")).filter(Boolean)));
    const typeNames = Array.from(group.typeSet || []);
    return {
      ...group,
      baseType: typeNames.length > 2 ? `${typeNames.slice(0, 2).join(", ")} +${typeNames.length - 2}` : typeNames.join(", ") || group.baseType,
      status,
      previewNumbers: uniqueNumbers.slice(0, 6),
      extraCount: Math.max(uniqueNumbers.length - 6, 0),
      rows: group.rows.sort((a, b) => Number(b.id || 0) - Number(a.id || 0)),
    };
  }).sort((a, b) => new Date(b.latestAt || 0) - new Date(a.latestAt || 0));
}

function getSessionLabel(user = {}) {
  const hasApp = Number(user.has_app_session || 0) > 0;
  const hasWeb = Number(user.has_web_session || 0) > 0;
  if (hasApp && hasWeb) return "App + Site";
  if (hasApp) return "App";
  if (hasWeb) return "Site";
  return "Offline";
}

function parseBetType(value = "") {
  const raw = String(value || "").toLowerCase();
  const session = raw.includes("close") ? "CLOSE" : "OPEN";
  const baseType = raw
    .replace(/[-_](open|close)$/i, "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .toUpperCase();
  return { baseType: baseType || "BET", session };
}

const statusPill = (status) => {
  const value = String(status || "").toLowerCase();
  if (value === "won" || value === "approved" || value === "success") return { ...pill, background: "#dcfce7", color: "#15803d" };
  if (value === "lost" || value === "rejected" || value === "blocked") return { ...pill, background: "#fee2e2", color: "#dc2626" };
  return { ...pill, background: "#f1f5f9", color: "#475569" };
};

const page = { display: "grid", gap: "18px" };
const topRow = { display: "flex", alignItems: "center", gap: "12px" };
const backButton = { width: "42px", height: "42px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", display: "grid", placeItems: "center", cursor: "pointer" };
const title = { margin: 0, fontSize: "18px", fontWeight: 800 };
const subtitle = { margin: "4px 0 0", color: "#64748b", fontSize: "12px" };
const editButton = { height: "42px", padding: "0 16px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, cursor: "pointer" };
const depositButton = { ...editButton, background: "#10b981", borderColor: "#10b981", color: "#fff" };
const flagButton = { ...editButton, color: "#b45309", borderColor: "#fde68a", background: "#fffbeb" };
const blockButton = { height: "42px", padding: "0 16px", border: 0, borderRadius: "12px", background: "#ef4444", color: "#fff", display: "flex", alignItems: "center", gap: "8px", fontWeight: 700, cursor: "pointer" };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "22px", padding: "22px", boxShadow: "0 10px 24px rgba(15,23,42,0.05)" };
const heroGrid = { display: "grid", gridTemplateColumns: "1fr 1fr 0.9fr", gap: "18px" };
const profileHeader = { display: "flex", justifyContent: "space-between", gap: "18px", marginBottom: "20px" };
const profileIdentity = { display: "flex", alignItems: "center", gap: "16px" };
const avatar = { width: "64px", height: "64px", borderRadius: "18px", display: "grid", placeItems: "center", background: "linear-gradient(135deg, #316ce8, #7c3aed)", color: "#fff", fontSize: "30px", fontWeight: 800 };
const avatarPhoto = { width: "64px", height: "64px", borderRadius: "18px", objectFit: "cover", display: "block", boxShadow: "0 10px 20px rgba(15,23,42,0.12)" };
const nameRow = { display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" };
const name = { fontSize: "18px", fontWeight: 800 };
const activeBadge = { padding: "6px 10px", borderRadius: "999px", background: "#dcfce7", color: "#15803d", fontWeight: 700, fontSize: "11px" };
const blockedBadge = { ...activeBadge, background: "#fee2e2", color: "#dc2626" };
const metaLine = { display: "flex", alignItems: "center", gap: "8px", color: "#475569" };
const tagBox = { alignSelf: "flex-start", padding: "8px 12px", borderRadius: "12px", background: "#eff6ff", color: "#2563eb", fontWeight: 700 };
const profileInfoGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "14px" };
const quickSettingsCard = { marginTop: "18px", paddingTop: "18px", borderTop: "1px solid #e2e8f0", display: "grid", gap: "14px" };
const quickSettingsGrid = { display: "grid", gridTemplateColumns: "1.1fr 1fr 1.4fr", gap: "16px", alignItems: "end" };
const quickField = { display: "grid", gap: "8px" };
const quickLabel = { color: "#64748b", fontSize: "13px", fontWeight: 700 };
const quickSelect = { minHeight: "42px", borderRadius: "10px", border: "1px solid #dbe4f0", padding: "0 12px", background: "#fff", fontWeight: 700, color: "#334155" };
const quickInputRow = { display: "flex", alignItems: "center", gap: "8px" };
const quickInput = { minHeight: "42px", width: "92px", borderRadius: "10px", border: "1px solid #dbe4f0", padding: "0 12px", background: "#fff", fontWeight: 800, color: "#0f172a" };
const quickHint = { color: "#94a3b8", fontSize: "12px", fontWeight: 700 };
const withdrawSegmentRow = { display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" };
const withdrawOption = (active) => ({
  minHeight: "40px",
  padding: "0 12px",
  borderRadius: "0",
  border: "1px solid #dbe4f0",
  background: active ? "#ef4444" : "#fff",
  color: active ? "#fff" : "#475569",
  fontWeight: 800,
  cursor: "pointer",
});
const quickActionRow = { display: "flex", justifyContent: "flex-end" };
const quickSaveButton = { minHeight: "42px", padding: "0 16px", borderRadius: "12px", border: 0, background: "#2563eb", color: "#fff", fontWeight: 800, cursor: "pointer" };
const networkTitle = { fontSize: "20px", fontWeight: 800, marginBottom: "20px" };
const networkGrid = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "14px" };
const infoItem = { display: "grid", gap: "6px" };
const infoLabel = { color: "#64748b", fontSize: "12px" };
const infoValue = { color: "#0f172a", fontSize: "14px" };
const balanceCard = { ...card, padding: 0, overflow: "hidden" };
const balanceRow = { minHeight: "58px", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", borderBottom: "1px solid #e5e7eb", color: "#0f172a", fontWeight: 700 };
const totalBalanceRow = { minHeight: "70px", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", background: "#316ce8", color: "#fff", fontWeight: 800, fontSize: "18px" };
const metricGrid = { display: "grid", gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "14px" };
const metricCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", padding: "18px", display: "grid", gap: "10px" };
const metricLabel = { color: "#64748b", fontSize: "12px", fontWeight: 700 };
const metricValue = { fontSize: "18px", fontWeight: 800 };
const riskRow = { display: "flex", gap: "18px", alignItems: "center", flexWrap: "wrap", color: "#334155", fontSize: "13px" };
const riskBadge = { padding: "6px 10px", borderRadius: "999px", background: "#dcfce7", color: "#15803d", fontWeight: 800 };
const filterBar = { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "14px", marginBottom: "18px", flexWrap: "wrap" };
const filterTabs = { display: "flex", gap: "8px", alignItems: "center" };
const filterChip = { height: "38px", padding: "0 14px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", color: "#334155", fontWeight: 700, cursor: "pointer" };
const activeFilterChip = { ...filterChip, background: "#316ce8", color: "#fff", borderColor: "#316ce8" };
const dateRangeRow = { display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" };
const dateLabel = { display: "flex", alignItems: "center", gap: "6px", color: "#64748b", fontSize: "13px", fontWeight: 700 };
const dateInput = { height: "40px", border: "1px solid #dbe4f0", borderRadius: "10px", padding: "0 12px", background: "#fff", color: "#0f172a", fontWeight: 700 };
const tabRow = { display: "flex", gap: "12px", marginBottom: "18px" };
const tabButton = { height: "42px", padding: "0 18px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", display: "flex", alignItems: "center", gap: "10px", fontWeight: 700, cursor: "pointer" };
const activeTab = { ...tabButton, background: "#316ce8", color: "#fff", borderColor: "#316ce8" };
const tabCount = { minWidth: "24px", height: "24px", borderRadius: "999px", background: "rgba(255,255,255,0.18)", display: "grid", placeItems: "center", fontSize: "12px", fontWeight: 800 };
const tableWrap = { overflowX: "auto" };
const bankPanel = { display: "grid", gap: "16px" };
const bankFormCard = { border: "1px solid #bfdbfe", borderRadius: "18px", background: "linear-gradient(135deg,#eff6ff,#ffffff)", padding: "16px", display: "grid", gap: "14px" };
const sectionTitle = { margin: 0, fontSize: "16px", fontWeight: 900, color: "#0f172a" };
const sectionHint = { margin: "4px 0 0", fontSize: "12px", color: "#64748b", fontWeight: 600 };
const bankFormGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" };
const bankActionRow = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" };
const checkLabel = { display: "inline-flex", alignItems: "center", gap: "8px", color: "#334155", fontWeight: 800, fontSize: "13px" };
const saveBankButton = { minHeight: "44px", border: 0, borderRadius: "12px", background: "linear-gradient(135deg,#16a34a,#22c55e)", color: "#fff", padding: "0 16px", fontWeight: 900, cursor: "pointer", boxShadow: "0 10px 22px rgba(22,163,74,.22)" };
const deleteBankButton = { minHeight: "34px", padding: "0 12px", border: "1px solid #fecaca", borderRadius: "10px", background: "#fff5f5", color: "#b91c1c", fontWeight: 800, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" };
const table = { width: "100%", borderCollapse: "collapse" };
const th = { textAlign: "left", height: "40px", padding: "0 12px", fontSize: "11px", color: "#64748b", textTransform: "uppercase", borderBottom: "1px solid #e5e7eb" };
const td = { padding: "14px 12px", borderBottom: "1px solid #f1f5f9", color: "#0f172a", fontSize: "14px" };
const groupSubline = { marginTop: "4px", color: "#2563eb", fontSize: "11px", fontWeight: 800 };
const mutedSubline = { marginTop: "4px", color: "#64748b", fontSize: "12px" };
const mutedSublineRed = { ...mutedSubline, color: "#dc2626", fontWeight: 800 };
const numberChipWrap = { display: "flex", gap: "6px", flexWrap: "wrap" };
const wideAdminNumberChip = { minHeight: "32px", borderRadius: "8px", display: "inline-grid", placeItems: "center", padding: "0 12px", background: "#eff6ff", color: "#2563eb", fontWeight: 900, whiteSpace: "nowrap" };
const moreCountChip = { ...wideAdminNumberChip, background: "#f8fafc", color: "#64748b" };
const viewGroupButton = { minHeight: "34px", padding: "0 12px", border: 0, borderRadius: "10px", background: "#0f172a", color: "#fff", fontWeight: 800, cursor: "pointer" };
const betGroupPanel = { display: "grid", gap: "8px", padding: "10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "14px" };
const betGroupItem = { display: "grid", gridTemplateColumns: "minmax(64px, .5fr) 1fr .8fr .8fr 1.3fr auto", gap: "10px", alignItems: "center", padding: "10px 12px", background: "#fff", borderRadius: "12px", border: "1px solid #edf2f7", fontSize: "13px" };
const smallBetEditButton = { minHeight: "32px", border: 0, borderRadius: "9px", background: "#eff6ff", color: "#2563eb", padding: "0 11px", fontWeight: 900, cursor: "pointer" };
const pill = { display: "inline-block", padding: "6px 10px", borderRadius: "999px", fontWeight: 700, fontSize: "11px" };
const removeEntryButton = { minHeight: "34px", padding: "0 12px", border: "1px solid #fecaca", borderRadius: "10px", background: "#fff5f5", color: "#b91c1c", fontWeight: 800, cursor: "pointer" };
const restoreEntryButton = { minHeight: "34px", padding: "0 12px", border: "1px solid #bbf7d0", borderRadius: "10px", background: "#f0fdf4", color: "#15803d", fontWeight: 800, cursor: "pointer" };
const deleteWrongEntryButton = { minHeight: "34px", padding: "0 12px", border: "1px solid #7f1d1d", borderRadius: "10px", background: "#7f1d1d", color: "#fff", fontWeight: 900, cursor: "pointer" };
const entryActionStack = { display: "flex", gap: "8px", flexWrap: "wrap" };
const emptyCell = { padding: "28px", textAlign: "center", color: "#94a3b8" };
const overlay = { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "grid", placeItems: "center", zIndex: 1000 };
const modal = { width: "480px", maxWidth: "92vw", background: "#fff", borderRadius: "22px", padding: "24px", display: "grid", gap: "14px" };
const walletModal = { ...modal, width: "560px" };
const modalTitle = { margin: 0, fontSize: "20px", fontWeight: 800 };
const modalHint = { margin: 0, color: "#64748b", fontSize: "12px", fontWeight: 800 };
const walletUserCard = { display: "grid", gridTemplateColumns: "64px 1fr auto", alignItems: "center", gap: "14px", padding: "14px", borderRadius: "16px", background: "#f8fafc", border: "1px solid #e2e8f0" };
const walletUserMeta = { display: "block", color: "#64748b", marginTop: "3px", fontSize: "12px" };
const walletBalance = { color: "#0f172a", whiteSpace: "nowrap" };
const walletModeGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px" };
const walletModeButton = { minHeight: "42px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", color: "#334155", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "6px", fontWeight: 800, cursor: "pointer" };
const walletModeActive = (color) => ({ ...walletModeButton, background: color, borderColor: color, color: "#fff" });
const quickAmountRow = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(82px, 1fr))", gap: "8px" };
const quickAmountButton = { minHeight: "38px", border: 0, borderRadius: "10px", background: "#f1f5f9", color: "#334155", fontWeight: 800, cursor: "pointer" };
const field = { display: "grid", gap: "8px", color: "#334155", fontWeight: 700 };
const input = { height: "52px", border: "1px solid #dbe4f0", borderRadius: "12px", padding: "0 14px", fontSize: "16px" };
const modalActions = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "10px" };
const ghostButton = { height: "48px", border: "1px solid #dbe4f0", borderRadius: "12px", background: "#fff", fontWeight: 700, cursor: "pointer" };
const saveButton = { height: "48px", border: 0, borderRadius: "12px", background: "#316ce8", color: "#fff", fontWeight: 700, cursor: "pointer" };
const keyboardWalletCss = `
@media (max-width: 900px) {
  .admin-keyboard-wallet-overlay {
    place-items: start center !important;
    align-items: start !important;
    overflow-y: auto !important;
    overscroll-behavior: contain;
    padding: calc(env(safe-area-inset-top) + 8px) 8px calc(env(safe-area-inset-bottom) + 12px) !important;
    box-sizing: border-box !important;
    -webkit-overflow-scrolling: touch;
  }
  .admin-profile-keyboard-wallet-modal {
    width: min(100%, 430px) !important;
    max-width: 100% !important;
    max-height: calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 16px) !important;
    border-radius: 16px !important;
    padding: 14px !important;
    gap: 10px !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
  @supports not (height: 100dvh) {
    .admin-profile-keyboard-wallet-modal {
      max-height: calc(100vh - 16px) !important;
    }
  }
  .admin-profile-keyboard-wallet-modal h3 {
    font-size: 18px !important;
  }
  .admin-profile-keyboard-wallet-user {
    grid-template-columns: 44px minmax(0, 1fr) auto !important;
    gap: 10px !important;
    padding: 10px !important;
    border-radius: 13px !important;
  }
  .admin-profile-keyboard-wallet-user > span:first-child {
    width: 44px !important;
    height: 44px !important;
    font-size: 17px !important;
  }
  .admin-profile-keyboard-wallet-user strong {
    font-size: 13px !important;
  }
  .admin-keyboard-wallet-modes {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    gap: 6px !important;
  }
  .admin-keyboard-wallet-modes button {
    min-height: 40px !important;
    font-size: 12px !important;
    padding: 0 6px !important;
  }
  .admin-keyboard-wallet-input {
    height: 46px !important;
    border-radius: 12px !important;
  }
  .admin-keyboard-wallet-quick {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    gap: 6px !important;
  }
  .admin-keyboard-wallet-quick button {
    min-height: 36px !important;
    font-size: 12px !important;
  }
  .admin-keyboard-wallet-footer {
    position: sticky !important;
    bottom: -14px !important;
    z-index: 5;
    background: #fff;
    box-shadow: 0 -10px 18px rgba(15,23,42,0.08);
    padding: 10px 0 calc(env(safe-area-inset-bottom) + 2px) !important;
    margin: 6px 0 -14px !important;
  }
  .admin-keyboard-wallet-footer button {
    height: 46px !important;
    min-height: 46px !important;
    border-radius: 12px !important;
  }
}
`;

export default AdminUserProfile;
